import { captureEvent } from "@/src/helpers/trackEvent";
import { updateConversationChats, convertHtmlToMarkdown, isJSON } from "@/src/helpers/utils";
import { estimateReportSeconds } from "@/src/helpers/reportTiers";

// Module-level handlers wired by ChatEventBridge in the React tree. Extra
// hooks (toast/modal) need React context which the singleton AiChat class
// can't access directly, so we register callbacks here and let the bridge
// component plug them in once on mount.
export const chatEventListeners = {
  onUsageLimitExceeded: null,
  onContextCompacted: null,
};

// A finished review needs to say so out loud: it streams no chat text, and
// leaving nothing is a legitimate outcome that looks identical to a hang.
export function reviewDoneToast(count) {
  return count > 0
    ? { title: `${count} comment${count === 1 ? "" : "s"} added` }
    : { title: "Review done", description: "Nothing here needed a note." };
}

export default class AiService {
  constructor(featureName) {
    this.featureName = featureName;
    this.connections = {};
  }

  // Shared event-routing for messages the server may send from any action
  // path (chat, review, toolbar, …). Returns true if the event was consumed
  // and the caller should stop processing (e.g. don't try to render it as a
  // comment / tool result). Safe to call from any onMessage handler.
  static tryHandleSystemEvent(data) {
    if (!data || typeof data !== "object" || !data.type) return false;
    if (data.type === "usage_limit_exceeded") {
      try {
        chatEventListeners.onUsageLimitExceeded?.(data);
      } catch (err) {
        console.warn("usage_limit_exceeded handler failed", err);
      }
      return true;
    }
    if (data.type === "context_compacted") {
      try {
        chatEventListeners.onContextCompacted?.(data);
      } catch (err) {
        console.warn("context_compacted handler failed", err);
      }
      return true;
    }
    return false;
  }

  createConnectionKey(keyParams) {
    let key = `AiFeature-${this.featureName}`;
    keyParams.forEach((param) => {
      if (param) key += `-${param}`;
    });
    return key;
  }

  setStreamingStatusChangeCallback(callback) {
    this.onStreamingStatusChange = callback;
  }

  createConnection(keyParams, getAccessTokenSilently, callbacks) {
    const connectionKey = this.createConnectionKey(keyParams);

    // Check if there's already a connection with the same key
    if (this.connections[connectionKey]) {
      console.error("Connection already exists for the given key parameters.");
      return;
    }

    // Claim the key BEFORE the async token fetch — two rapid starts otherwise
    // both pass the check above and the loser leaks an untracked socket.
    // Closing the claim behaves like a socket close, so a stop during the
    // token fetch still settles the caller instead of hanging it.
    const claim = {
      readyState: WebSocket.CONNECTING,
      close: () => {
        if (this.connections[connectionKey] === claim) {
          delete this.connections[connectionKey];
          if (callbacks.onClose) callbacks.onClose();
        }
      },
    };
    this.connections[connectionKey] = claim;

    getAccessTokenSilently()
      .then((token) => {
        // Closed (or replaced) while the token was being fetched.
        if (this.connections[connectionKey] !== claim) return;
        const ws = new WebSocket(process.env.NEXT_PUBLIC_WSS_SERVER);
        this.connections[connectionKey] = ws;
        // A failed socket fires `error` and THEN `close` (1006), so without
        // this one failure would be counted twice.
        let failureReported = false;
        const reportFailure = (props) => {
          if (failureReported) return;
          failureReported = true;
          captureEvent("chat_ws_failed", { ...props, feature: this.featureName });
        };

        if (callbacks.beforeStart) callbacks.beforeStart();

        ws.onopen = () => {
          if (this.onStreamingStatusChange) this.onStreamingStatusChange(connectionKey, true);

          if (callbacks.onConnectionOpen) callbacks.onConnectionOpen(keyParams, token);
        };

        ws.onmessage = (event) => {
          if (callbacks.onMessage) callbacks.onMessage(event);
        };

        ws.onclose = (event) => {
          if (this.onStreamingStatusChange) this.onStreamingStatusChange(connectionKey, false);

          // 1000/1001 are normal teardown; anything else is a failure nothing
          // else records — the socket never reaches the backend's access log.
          if (event.code !== 1000 && event.code !== 1001) {
            reportFailure({ reason: "close", code: event.code });
          }

          try {
            if (callbacks.onClose) callbacks.onClose(event);
          } finally {
            delete this.connections[connectionKey];
          }
        };

        ws.onerror = (event) => {
          reportFailure({ reason: "error" });
          if (callbacks.onError) callbacks.onError(event);
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        };
      })
      .catch((error) => {
        console.error(`Error getting access token for ${this.featureName}:`, error);
        if (this.connections[connectionKey] === claim) delete this.connections[connectionKey];
        if (callbacks.onError) callbacks.onError(error);
      });
  }

  sendMessage(keyParams, message) {
    const connectionKey = this.createConnectionKey(keyParams);
    const ws = this.connections[connectionKey];
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (typeof message === "object") {
        ws.send(JSON.stringify(message));
      } else {
        ws.send(message);
      }
    } else {
      console.error(`Cannot send message, connection for ${this.featureName} is not open.`);
    }
  }

  closeConnection(keyParams) {
    const connectionKey = this.createConnectionKey(keyParams);
    const ws = this.connections[connectionKey];
    if (ws) {
      ws.close();
    }
  }

  // Graceful stop: ask the server to end the turn instead of slamming the
  // socket shut. The server cancels the in-flight LLM stream, persists the
  // partial reply, and sends agent_end(stop_reason="user_stop") — so the
  // text streamed so far is kept and the conversation stays consistent.
  // Closing the socket instead (old behavior) leaves the server generating
  // the full reply detached, burning tokens the user asked us not to spend.
  // Falls back to close() when the socket isn't open (e.g. still connecting);
  // the server treats that as a disconnect and finishes detached.
  stopStreaming(keyParams) {
    const connectionKey = this.createConnectionKey(keyParams);
    const ws = this.connections[connectionKey];
    if (!ws) return;
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: "StopStream" }));
      // Server finishes the turn (agent_end) and closes; onclose runs the
      // usual cleanup. No client-side close needed.
    } else {
      ws.close();
    }
  }
}

export class AiChat extends AiService {
  static getInstance() {
    if (!this.instance) {
      this.instance = new AiChat("Brainstorm");
    }
    return this.instance;
  }

  async start(
    getAccessTokenSilently,
    bookId,
    chapterId,
    lastCursorPositionInEditor,
    activeConversation,
    addCommentsToHtml,
    setConversations,
    openExpressionEnabled,
    selectedSources,
    selectedModel,
    pendingImages = [],
    atlasReadOnly = false, // Atlas read-only: the turn never writes to the Atlas/notes.
    chapterReadOnly = false, // Chapter read-only: the turn cannot stage manuscript edits.
    overrideUserInput = null, // When set (e.g. on retry), bypass the DOM read.
    rewindFromMessageId = null, // When set, server rewinds the active leaf
    // past this message_id before inserting the new turn (retry / edit flow).
  ) {
    const keyParams = [bookId, chapterId, activeConversation];

    return new Promise((resolve, reject) => {
      // Global variables for the WebSocket
      let userResponsePreferance = "";
      const element = document.getElementById("Brainstorm-Input-ID");

      // On retry the user input comes from the server (prior user bubble); on
      // normal send, it comes from the editor input field.
      const userInput =
        overrideUserInput !== null
          ? overrideUserInput
          : element.tagName === "TEXTAREA"
            ? element.value
            : convertHtmlToMarkdown(element.innerHTML); // Convert to Markdown cause WSS, Backend, and Chat history is all in Markdown
      this.setConversations = setConversations;

      if (userInput === null || userInput === "") {
        resolve();
        return;
      }

      const beforeStart = () => {
        // Edit / retry: drop everything from the rewind target forward so the
        // UI matches what the server will produce.
        if (rewindFromMessageId != null) {
          setConversations((prev) =>
            updateConversationChats(prev, activeConversation, { message_id: rewindFromMessageId }, "optimistic_rewind"),
          );
        }

        // DOM clear only on a fresh send — retry/edit don't touch the input.
        if (overrideUserInput === null) {
          if (element.tagName === "TEXTAREA") {
            element.value = "";
          } else {
            element.removeAttribute("contenteditable");
            element.innerHTML = null;
            element.setAttribute("contenteditable", "true");
          }
        }

        // Optimistic insert — fresh send, retry, and edit ALL get the user
        // bubble + assistant loading placeholder to appear instantly. The
        // reducer's optimistic-replacement logic swaps these for the real
        // rows when message_start events arrive over WS.
        const imageUrlsForOptimistic = Array.isArray(pendingImages) ? pendingImages.filter(Boolean) : [];
        const optimisticUserMessage = imageUrlsForOptimistic.length
          ? `${imageUrlsForOptimistic.map((u) => `![image](${u})`).join("\n")}\n\n${userInput}`
          : userInput;

        setConversations((prev) =>
          updateConversationChats(prev, activeConversation, { tempId: -Date.now(), content: optimisticUserMessage }, "optimistic_user"),
        );
      };

      const onConnectionOpen = async (keyParams, token) => {
        // pendingImages are already S3/CloudFront URLs uploaded at attach time.
        const imageUrls = Array.isArray(pendingImages) ? pendingImages.filter(Boolean) : [];

        // Fired after imageUrls exists, so the event can report them — the same
        // event name as before, with properties added, so existing dashboards
        // keep working.
        captureEvent("chat_message_sent", {
          image_count: imageUrls.length,
          model_id: selectedModel ?? "auto",
        });

        let clientTimezone = null;
        try {
          clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
          clientTimezone = null;
        }

        const initialMessage = {
          action: "Brainstorm",
          data: userInput,
          book_id: bookId,
          chapter_id: chapterId,
          chapter_cursor_position: lastCursorPositionInEditor,
          token: token,
          conversation_id: activeConversation,
          open_expression: openExpressionEnabled,
          selected_sources: selectedSources,
          selected_model: selectedModel,
          client_timezone: clientTimezone,
          ...(imageUrls.length > 0 && { images: imageUrls }),
          ...(rewindFromMessageId != null && { rewind_from_message_id: rewindFromMessageId }),
          ...(atlasReadOnly && { atlas_read_only: true }),
          ...(chapterReadOnly && { chapter_read_only: true }),
        };
        this.sendMessage(keyParams, initialMessage);
      };

      const onMessage = async (e) => {
        // Every message is a structured agent event.
        if (!isJSON(e.data)) return;
        let event;
        try {
          event = JSON.parse(e.data);
        } catch (err) {
          console.error("Bad event payload:", err, e.data);
          return;
        }

        switch (event.type) {
          case "agent_start":
            // Conversation id may have been minted server-side; the caller
            // swaps us to the real id when they reload the list. We also
            // dispatch into the reducer so it can mark the turn as in-
            // flight — the orphan-tool-call synthesis in ChatList gates on
            // that flag to avoid flashing "error" on tools that just
            // haven't reported back yet.
            setConversations((prev) => updateConversationChats(prev, activeConversation, {}, "event_agent_start"));
            return;

          case "message_start":
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                { message_id: event.message_id, kind: event.role, parent_id: event.parent_id },
                "event_message_start",
              ),
            );
            return;

          case "message_update": {
            const delta = event.delta || {};
            if (delta.kind === "text" && delta.text) {
              setConversations((prev) =>
                updateConversationChats(
                  prev,
                  activeConversation,
                  { message_id: event.message_id, text: delta.text },
                  "event_message_update_text",
                ),
              );
            } else if (delta.kind === "tool_call_partial" && delta.arguments_delta) {
              // The server only streams edit_chapter arguments — these
              // accumulate on the row and render as ghost edit cards.
              setConversations((prev) =>
                updateConversationChats(
                  prev,
                  activeConversation,
                  {
                    message_id: event.message_id,
                    index: delta.index || 0,
                    arguments_delta: delta.arguments_delta,
                  },
                  "event_message_update_tool_call",
                ),
              );
            }
            return;
          }

          case "message_end":
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                {
                  message_id: event.message_id,
                  content: event.content,
                  tool_calls: event.tool_calls,
                  token_usage: event.token_usage,
                },
                "event_message_end",
              ),
            );
            return;

          case "tool_start":
            // The assistant row's `tool_calls` (from message_end) already
            // carries this tool's name + arguments — the chip renders from
            // there. The dedicated start event is informational only for now.
            return;

          case "tool_end":
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                {
                  tool_call_id: event.tool_call_id,
                  result_message_id: event.result_message_id,
                  status: event.status,
                  duration_ms: event.duration_ms,
                },
                "event_tool_end",
              ),
            );
            return;

          case "compaction":
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                {
                  message_id: event.message_id,
                  summary: event.summary,
                  tokens_before: event.tokens_before,
                  first_kept_id: event.first_kept_id,
                },
                "event_compaction",
              ),
            );
            return;

          case "thinking": {
            const text = (event.text || "").trim();
            if (!text) return;
            setConversations((prev) =>
              updateConversationChats(prev, activeConversation, { text, kind: event.kind, tool: event.tool }, "event_thinking"),
            );
            return;
          }

          case "usage_limit_exceeded":
            // Daily token cap reached. Global handler shows the limit modal;
            // we also pin an error flag on the active conversation so the
            // chat input switches to a non-retryable error state.
            AiService.tryHandleSystemEvent(event);
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                { message: event.message || "Daily token limit reached", canRetry: false },
                "error_flag",
              ),
            );
            return;

          case "progress":
            setConversations((prev) =>
              updateConversationChats(prev, activeConversation, { message: event.message || "" }, "event_progress"),
            );
            return;

          case "error":
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                { message: event.message || "Something went wrong.", retryable: event.retryable },
                "event_error",
              ),
            );
            return;

          case "agent_end":
            setConversations((prev) =>
              updateConversationChats(
                prev,
                activeConversation,
                { leaf_message_id: event.leaf_message_id, total_tokens: event.total_tokens },
                "event_agent_end",
              ),
            );
            return;

          case "annotation":
            // A single inline margin comment from the agent's annotate_text tool.
            // Reuse the comment-render path: anchor by the verbatim quote
            // (anchor_text); the full card (title/message/rewritten_text) is
            // reconciled from the DB by id via useDetectComments.
            addCommentsToHtml([{ id: event.comment_id, sentence: event.anchor_text, critique: event.title, content: event.message, rewritten_text: event.rewritten_text }]);
            return;

          default:
            // Unknown event — let the global system-event handler try
            // (covers system events that aren't modeled in this switch yet).
            AiService.tryHandleSystemEvent(event);
            return;
        }
      };

      const onError = (error) => {
        setConversations((prev) =>
          updateConversationChats(
            prev,
            activeConversation,
            { message: "Something went wrong while generating this response.", retryable: true },
            "event_error",
          ),
        );
        console.error(error);
        reject(error);
      };

      const onClose = () => {
        // Sweep up any tool calls that never received a tool_end and any
        // assistant placeholder still flagged streaming. Without this, a
        // dropped WS would leave chips stuck on "running…" forever.
        setConversations((prev) => updateConversationChats(prev, activeConversation, {}, "event_stream_terminated"));
        resolve();
      };

      this.createConnection(keyParams, getAccessTokenSilently, { beforeStart, onConnectionOpen, onMessage, onError, onClose });
    });
  }

  closeConnection(keyParams) {
    super.closeConnection(keyParams);
    const activeConversation = keyParams[2];
    this.setConversations((prev) =>
      updateConversationChats(prev, activeConversation, { message: "Connection closed.", retryable: true }, "event_error"),
    );
  }

  /**
   * Retry the most recent assistant response. Finds the latest user row in
   * the local state, optimistically rewinds the chat list, and re-sends via
   * WSS with `rewindFromMessageId`. The server creates a sibling user row +
   * fresh assistant turn. WSS-only — no HTTP round-trip.
   *
   * Caller passes the active conversation object so we can read its chats
   * array directly — avoids a stale-closure race if conversations state has
   * updated since the button rendered.
   */
  async retry({
    getAccessTokenSilently,
    bookId,
    chapterId,
    lastCursorPositionInEditor,
    activeConversation,
    activeConversationData,
    addCommentsToHtml,
    setConversations,
    openExpressionEnabled,
    selectedSources,
    selectedModel,
    atlasReadOnly = false,
    chapterReadOnly = false,
  }) {
    // Every conversation lives in agent_messages now (the bulk migration
    // backfilled prior data, and `get_conversations_list` schedules a
    // per-book backfill on every load as a safety net). The legacy
    // chats-table retry path was removed once that invariant held.
    const lastUser = [...(activeConversationData?.chats || [])].reverse().find((row) => row.kind === "user");
    if (!lastUser) throw new Error("No prior user message to retry");

    return this.start(
      getAccessTokenSilently,
      bookId,
      chapterId,
      lastCursorPositionInEditor,
      activeConversation,
      addCommentsToHtml,
      setConversations,
      openExpressionEnabled,
      selectedSources,
      selectedModel,
      [],
      atlasReadOnly,
      chapterReadOnly,
      lastUser.content || "",
      lastUser.message_id,
    );
  }

  /**
   * Edit a user message and re-run from that point. Same mechanism as retry,
   * but the content is the user's edited string and the rewind target is the
   * specific user message they're editing. Only supported on new conversations.
   */
  async editAndRetry({
    getAccessTokenSilently,
    bookId,
    chapterId,
    lastCursorPositionInEditor,
    activeConversation,
    activeConversationData,
    addCommentsToHtml,
    setConversations,
    openExpressionEnabled,
    selectedSources,
    selectedModel,
    messageId,
    newContent,
    atlasReadOnly = false,
    chapterReadOnly = false,
  }) {
    if (!newContent || !newContent.trim()) {
      throw new Error("Edited message can't be empty");
    }
    return this.start(
      getAccessTokenSilently,
      bookId,
      chapterId,
      lastCursorPositionInEditor,
      activeConversation,
      addCommentsToHtml,
      setConversations,
      openExpressionEnabled,
      selectedSources,
      selectedModel,
      [],
      atlasReadOnly,
      chapterReadOnly,
      newContent,
      messageId,
    );
  }
}

// Ticker pacing: ~45s typical full run, climbing toward 90; only onClose
// reaches 100.
const COMMENTS_TYPICAL_RUN_SECONDS = 45;
const COMMENTS_PROGRESS_CEILING = 90;

export class AiComments extends AiService {
  static getInstance() {
    if (!this.instance) {
      this.instance = new AiComments("Comments");
    }
    return this.instance;
  }

  async start(
    getAccessTokenSilently,
    bookId,
    chapterId,
    addCommentsToHtml,
    // The review room's useLoadingProgress state (optional: no progress UI).
    loadingState,
    // A highlighted passage scopes the review; empty = whole chapter.
    selection = "",
  ) {
    const keyParams = [bookId, chapterId];

    return new Promise((resolve, reject) => {
      // Without this, createConnection refuses the duplicate key and the
      // promise hangs forever.
      if (this.connections[this.createConnectionKey(keyParams)]) {
        reject(new Error("A review is already running for this chapter."));
        return;
      }
      // Overlapping runs (other chapters) share ONE bar — the newest run owns
      // it; a stale run's callbacks are silently dropped. Claimed only after
      // the duplicate guard, so a rejected start can't disown the live run.
      this.activeRunId = (this.activeRunId || 0) + 1;
      const runId = this.activeRunId;
      const own = (fn) => (...args) => {
        if (runId === this.activeRunId) fn?.(...args);
      };
      const setProgress = own(loadingState?.setLoadingProgress);
      const setLimit = own(loadingState?.setProgressLimit);
      const setMessage = own(loadingState?.setLoadingMessage);
      const setEstimate = own(loadingState?.setEstimateTotalTime);

      // Arm the bar on click, not on socket-open: the agent is silent, so the
      // ticker (limit + estimate) is what keeps it moving between annotations.
      setProgress(5);
      setLimit(COMMENTS_PROGRESS_CEILING);
      setEstimate(COMMENTS_TYPICAL_RUN_SECONDS);
      setMessage("Starting review...");

      const onConnectionOpen = (keyParams, token) => {
        captureEvent("review_comments_triggered", {
          scope: selection ? "selection" : "chapter",
        });
        setMessage(selection ? "Reviewing the selection..." : "Reviewing your chapter...");
        const initialMessage = {
          action: "Comment",
          data: "",
          book_id: bookId,
          chapter_id: chapterId,
          token: token,
          selection,
        };
        this.sendMessage(keyParams, initialMessage);
      };

      let commentCount = 0;
      // agent_end arrives even on silent runs; a close without it is a
      // dropped socket, not a finished review.
      let runEnded = false;
      const onMessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (AiService.tryHandleSystemEvent(data)) return;
          if (data.type === "agent_end") {
            runEnded = true;
            return;
          }
          if (data.type === "annotation") {
            // Anchor by verbatim quote; the full card reconciles from the DB
            // by id via useDetectComments.
            addCommentsToHtml([{ id: data.comment_id, sentence: data.anchor_text, critique: data.title, content: data.message, rewritten_text: data.rewritten_text }]);
            commentCount += 1;
            // No progress bump: time paces the bar; the message (and the note
            // landing in the margin) is the activity signal.
            setMessage(`Added ${commentCount} comment${commentCount === 1 ? "" : "s"}...`);
          } else if (data.type === "progress_update") {
            setLimit((prevLimit) => {
              setProgress(prevLimit);
              return prevLimit;
            });
            setMessage(data.message);
            setEstimate(data.total_time_estimate);
          }
        } catch (e) {
          console.error("Error parsing JSON data:", e);
        }
      };

      const onClose = () => {
        setProgress(100);
        if (runEnded) {
          resolve(commentCount);
        } else {
          reject(Object.assign(new Error("Connection lost mid-review."), { interrupted: true, added: commentCount }));
        }
      };

      const onError = (error) => {
        console.error(error);
        setProgress(100);
        reject(error);
      };

      this.createConnection(keyParams, getAccessTokenSilently, { onConnectionOpen, onMessage, onClose, onError });
    });
  }
}

// Agentic reports read the manuscript before writing: budget real time.
export class AiReport extends AiService {
  static getInstance() {
    if (!this.instance) {
      this.instance = new AiReport("Report");
    }
    return this.instance;
  }

  async start(
    getAccessTokenSilently,
    bookId,
    chapterId,
    reportVersion,
    // The reports tab's useLoadingProgress state (page-owned, so the bar
    // survives tab switches).
    loadingState,
    // Author notes ride the GenerateReport payload verbatim; the agent reads
    // them as part of the commission.
    notes = "",
    // The scope's word count — the countdown scales with the book.
    wordCount = 0,
  ) {
    const keyParams = [bookId, chapterId];
    let reportData = null;
    let runEnded = false;
    let stopped = false;
    let serverError = null;
    // One lifecycle event per run: triggered -> completed | stopped | failed.
    const startedAt = Date.now();
    const eventProps = { tier: reportVersion, scope: chapterId ? "chapter" : "book", has_notes: Boolean(notes) };
    let outcomeTracked = false;
    const trackOutcome = (name, props = {}) => {
      if (outcomeTracked) return;
      outcomeTracked = true;
      captureEvent(name, { ...eventProps, duration_seconds: Math.round((Date.now() - startedAt) / 1000), ...props });
    };

    return new Promise((resolve, reject) => {
      const key = this.createConnectionKey(keyParams);
      const existing = this.connections[key];
      if (existing && existing.readyState !== WebSocket.OPEN && existing.readyState !== WebSocket.CONNECTING) {
        // A socket that died without a close frame (sleep, stall) would
        // otherwise hold the key — and this guard — until a page reload.
        existing.close();
        delete this.connections[key];
      } else if (existing) {
        reject(new Error("A report is already running for this scope."));
        return;
      }
      // A stopped run's close arrives async — it must not touch the bar a
      // newer run owns. Same ownership scheme as AiComments.
      this.activeRunId = (this.activeRunId || 0) + 1;
      const runId = this.activeRunId;
      const own = (fn) => (...args) => {
        if (runId === this.activeRunId) fn?.(...args);
      };
      const setProgress = own(loadingState?.setLoadingProgress);
      const setLimit = own(loadingState?.setProgressLimit);
      const setMessage = own(loadingState?.setLoadingMessage);
      const setEstimate = own(loadingState?.setEstimateTotalTime);

      // Arm the bar before any async step: the run button reads progress > 0
      // as "running", so the guard must flip in the same tick as the click.
      setProgress(5);
      setLimit(95);
      setMessage("Starting report...");
      setEstimate(estimateReportSeconds(reportVersion, wordCount, Boolean(notes)));

      const onConnectionOpen = (keyParams, token) => {
        captureEvent("review_report_triggered", eventProps);
        setMessage("Scribe is reading...");
        this.sendMessage(keyParams, {
          action: "GenerateReport",
          data: reportVersion,
          notes,
          book_id: bookId,
          chapter_id: chapterId,
          token: token,
        });
      };

      const onMessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (AiService.tryHandleSystemEvent(data)) return;
          if (data.type === "agent_end") {
            runEnded = true;
            stopped = data.stop_reason === "user_stop";
          } else if (data.type === "error") {
            serverError = data.message || "The report failed.";
          } else if (data.type === "report") {
            // One typed, self-contained event from finish_report, shaped like
            // a Reports row so saved and fresh reports render identically.
            reportData = {
              id: data.report_id,
              book_id: data.book_id,
              chapter_id: data.chapter_id,
              type: data.report_type,
              content_json: JSON.stringify(data.content),
            };
          }
        } catch (error) {
          console.error("Error parsing message:", error);
        }
      };

      // Success means a report actually arrived — an errored, stopped, or
      // dropped run must not read as "Report generated" (or burn quota).
      const onClose = () => {
        setProgress(100);
        setEstimate(0);
        if (reportData && !serverError) {
          setMessage("Report generated");
          trackOutcome("report_completed");
          resolve(reportData);
          return;
        }
        setMessage("");
        if (stopped) trackOutcome("report_stopped");
        else trackOutcome("report_failed", { reason: serverError ? "server_error" : runEnded ? "no_report" : "connection_lost" });
        reject(
          Object.assign(
            new Error(
              serverError ||
                (stopped ? "Stopped." : runEnded ? "The run ended without a report." : "Connection lost before the report arrived."),
            ),
            { stopped, interrupted: !serverError && !runEnded },
          ),
        );
      };

      const onError = (error) => {
        console.error(error);
        setProgress(100);
        setMessage("Error generating report");
        trackOutcome("report_failed", { reason: "connection_error" });
        reject(error);
      };

      this.createConnection(keyParams, getAccessTokenSilently, { onConnectionOpen, onMessage, onClose, onError });
    });
  }

  // Stop must work after a panel remount, when the exact key the run
  // connected with (book scope drops the chapter id) is no longer known:
  // gracefully stop every report run for this book. StopStream lets the
  // server cancel under its lease and answer agent_end(user_stop); slamming
  // the socket leaves it generating detached with the lease held.
  stopScope(bookId) {
    const prefix = this.createConnectionKey([bookId]);
    const keys = Object.keys(this.connections).filter((k) => k === prefix || k.startsWith(`${prefix}-`));
    keys.forEach((k) => {
      const ws = this.connections[k];
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "StopStream" }));
      else ws.close();
    });
    return keys.length;
  }
}

export class AiToolBar extends AiService {
  static getInstance() {
    if (!this.instance) {
      this.instance = new AiToolBar("ToolBar");
    }
    return this.instance;
  }

  // Streams a toolbar action (Elaborate/Embellish/Concise/Restructure) and
  // reports progress through callbacks — never touches the editor. The chunks
  // are plain-text appends; onChunk receives the full accumulated text so far.
  // Two server frames drive the finish: `editor_done` fires onDone(text,
  // { complete: true }) — the flip-to-preview signal, sent before persistence
  // so there's no DB wait; `editor_saved` fires onSaved(messageId) a beat
  // later with the rate handle. A close without editor_done fires onDone(...,
  // { complete: false }) (drop/stop). Returns { stop } which closes the socket.
  start({
    action,
    inputText,
    guidance,
    contextPlacement,
    contextSize,
    bookId,
    chapterId,
    openExpressionEnabled,
    getAccessTokenSilently,
    onChunk,
    onDone,
    onSaved,
    onError,
  }) {
    const keyParams = ["AiToolbar-" + new Date().getTime()];
    let currentContent = "";
    let stopped = false;
    let errored = false;
    // onDone fires exactly once: on the editor_done frame (clean finish,
    // complete=true) OR on a close that never saw it (drop/stop, complete=false).
    let doneFired = false;

    const onConnectionOpen = (keyParams, token) => {
      if (stopped) {
        this.closeConnection(keyParams);
        return;
      }
      captureEvent("highlight_bar_stream_started", { action });
      const msg = JSON.stringify({
        action: action,
        data: inputText,
        // Optional steers for the rewrite; undefined is dropped by
        // JSON.stringify, so older servers see an unchanged payload.
        guidance: guidance || undefined,
        context_placement: contextPlacement,
        context_size: contextSize,
        book_id: bookId,
        chapter_id: chapterId,
        token: token,
        open_expression: openExpressionEnabled,
      });
      this.sendMessage(keyParams, msg);
    };

    const fireDone = (complete) => {
      if (doneFired) return;
      doneFired = true;
      if (onDone) onDone(currentContent, { complete });
    };

    const onMessage = (event) => {
      if (isJSON(event.data)) {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.type === "editor_done") {
            // Flip to the applyable preview NOW — the server sends this before
            // persisting, so the DB write never sits on the Accept-ready path.
            fireDone(true);
            return;
          }
          if (parsed?.type === "editor_saved") {
            // The saved-message handle, hydrated a beat after editor_done.
            if (onSaved && parsed.message_id != null) onSaved(parsed.message_id);
            return;
          }
          AiService.tryHandleSystemEvent(parsed);
        } catch (e) {
          // ignore
        }
        return;
      }
      currentContent += event.data;
      if (onChunk) onChunk(currentContent);
    };

    const onCloseWs = () => {
      if (errored) return;
      // No editor_done seen before close → the stream dropped (or the user
      // stopped): deliver whatever streamed, marked incomplete.
      fireDone(false);
    };

    const onErrorWs = (error) => {
      console.error("AiToolBar stream error:", error);
      errored = true;
      if (onError) onError(error);
    };

    this.createConnection(keyParams, getAccessTokenSilently, {
      onConnectionOpen,
      onMessage,
      onClose: onCloseWs,
      onError: onErrorWs,
    });

    return {
      stop: () => {
        stopped = true;
        this.closeConnection(keyParams);
      },
    };
  }
}

// AiDetectCategory / AiChapterSummary / AiUpdateCharacters used to live here,
// wired to the now-removed category/characters/summaries entries in
// knowledgeConfig.js. Deleted alongside them — see that file's header comment
// for why (all three were unreachable: category never actually triggered
// since nothing live ever marked it "fetched"; the other two already carried
// their own "unreachable in the current app" comments).

export class AiVersionDescription extends AiService {
  static getInstance() {
    if (!this.instance) {
      this.instance = new AiVersionDescription("VersionDescription");
    }
    return this.instance;
  }

  async start(getAccessTokenSilently, versionId, bookId, setVersions = null, versions = null) {
    const keyParams = [versionId];
    let description = ""; // Keep track of description if no UI updates needed

    return new Promise((resolve, reject) => {
      const onConnectionOpen = (keyParams, token) => {
        captureEvent("version_description_triggered");
        const initialMessage = {
          action: "WriteVersionDescription",
          data: "",
          book_id: bookId,
          version_id: versionId,
          token: token,
        };
        this.sendMessage(keyParams, initialMessage);
      };

      const onMessage = (event) => {
        if (isJSON(event.data)) {
          try {
            if (AiService.tryHandleSystemEvent(JSON.parse(event.data))) return;
          } catch (e) {
            // ignore — falls through to streaming text path
          }
        }
        if (setVersions && versions) {
          // Update UI in real-time if we have the state setters
          setVersions((currentVersions) => {
            return currentVersions.map((version) => {
              if (version.id === versionId) {
                return {
                  ...version,
                  description: (version.description || "") + event.data,
                };
              }
              return version;
            });
          });
        } else {
          // Otherwise just accumulate the description
          description += event.data;
        }
      };

      const onClose = () => {
        // If we weren't updating UI, return the accumulated description
        resolve(setVersions && versions ? undefined : description);
      };

      const onError = (error) => {
        console.error("WebSocket error in VersionDescription:", error);
        reject(error);
      };

      this.createConnection(keyParams, getAccessTokenSilently, {
        onConnectionOpen,
        onMessage,
        onClose,
        onError,
      });
    });
  }
}

/**
 * AiContinueWriting — the seal's Continue, as a full agent turn that
 * delivers prose via the terminal write_prose tool call (reply text stays
 * hidden). One socket and one FRESH hidden kind="writing" conversation per
 * press; frames are forwarded raw to `onEvent` (the useContinueWriting hook
 * owns the streaming semantics).
 */
export class AiContinueWriting extends AiService {
  static getInstance() {
    if (!this.instance) {
      this.instance = new AiContinueWriting("ContinueWriting");
    }
    return this.instance;
  }

  start({ bookId, chapterId, userInput, selectedModel, openExpressionEnabled, getAccessTokenSilently, onEvent, onClose, onError }) {
    const keyParams = ["ContinueWriting-" + Date.now()];
    // Stop pressed before the socket exists (token still fetching): remember
    // it and abort at open instead of silently doing nothing.
    let stopRequested = false;

    const onConnectionOpen = (kp, token) => {
      if (stopRequested) {
        this.closeConnection(keyParams);
        return;
      }
      this.sendMessage(kp, {
        action: "ContinueWriting",
        data: userInput,
        book_id: bookId,
        chapter_id: chapterId,
        token: token,
        // Always null: every press is a fresh writing conversation.
        conversation_id: null,
        open_expression: openExpressionEnabled,
        // undefined drops out of JSON.stringify — older servers see an
        // unchanged payload.
        selected_model: selectedModel || undefined,
      });
    };

    const onMessage = (e) => {
      if (!isJSON(e.data)) return;
      let event;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      if (AiService.tryHandleSystemEvent(event)) return;
      onEvent?.(event);
      // The turn is over — the server closes too, but don't wait on it.
      if (event.type === "agent_end") this.closeConnection(keyParams);
    };

    this.createConnection(keyParams, getAccessTokenSilently, { onConnectionOpen, onMessage, onClose, onError });
    return {
      stop: () => {
        stopRequested = true;
        this.stopStreaming(keyParams);
      },
      // Hard fallback: kill the socket outright (a wedged server can't
      // acknowledge a graceful stop). onClose runs the usual keep-what-
      // arrived path.
      close: () => this.closeConnection(keyParams),
    };
  }
}
